import { api } from '../../fetchWrapper';
import { API_ENDPOINTS, getEndpoint } from '../../apiConstants';
import type { ErrorPatternData } from './errorPattern.types';
import { fetchClients } from '../clientData/clientApi';

// GET error patterns
export async function fetchErrorPatterns({
  clientCode,
  firstResult = 0,
  maxResult = 10,
  searched = true,
  clientId,
  isTechnicalError,
  errorCode,
}: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
  searched?: boolean;
  clientId?: number;
  isTechnicalError?: string;
  errorCode?: string;
}): Promise<ErrorPatternData[]> {
  const params: Record<string, any> = {
    firstResult,
    maxResult,
    searched,
  };

  if (clientId) params.clientId = clientId;
  if (isTechnicalError) params.isTechnicalError = isTechnicalError;
  if (errorCode) params.errorCode = errorCode;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.ERROR_PATTERN.LIST);
  const errorPatterns = await api.get<any[]>(endpoint, params);
  
  // Fetch clients to map clientId to clientName
  const clients = await fetchClients({ 
    clientCode: 'admin', 
    firstResult: -1, 
    maxResult: 0 
  });
  
  // Create clientId to clientName mapping
  const clientMap: Record<string, string> = {};
  clients.forEach((client: any) => {
    clientMap[client.id] = client.name;
  });
  
  // Transform error patterns to include clientName; handle both object and array-of-arrays formats
  return errorPatterns.map((pattern: any) => {
    let id: number | undefined;
    let clientId: number | undefined;
    let patternText: string;
    let errorCodeVal: string;
    let isTechnicalError: string | boolean | undefined;

    if (Array.isArray(pattern) && pattern.length >= 5) {
      // Array format: [id, clientId, pattern, errorCode, isTechnicalError, ...]
      id = pattern[0];
      clientId = pattern[1];
      patternText = pattern[2] ?? '';
      errorCodeVal = pattern[3] ?? '';
      isTechnicalError = pattern[4];
    } else if (typeof pattern === 'object') {
      id = pattern.id ?? pattern.ID;
      clientId = pattern.clientId ?? pattern.clientID;
      patternText = pattern.pattern ?? '';
      errorCodeVal = pattern.errorCode ?? '';
      isTechnicalError = pattern.isTechnicalError ?? pattern.errorType;
    } else {
      return { id: 0, clientId: 0, pattern: '', errorCode: '', clientName: 'Unknown Client' };
    }

    return {
      id,
      clientId,
      pattern: patternText,
      errorCode: errorCodeVal,
      isTechnicalError,
      clientName: clientMap[clientId ?? 0] || 'Unknown Client',
    };
  });
}

// GET error patterns count
export async function fetchErrorPatternsCount({
  clientCode,
  searched = true,
  clientId,
  isTechnicalError,
  errorCode,
}: {
  clientCode: string;
  searched?: boolean;
  clientId?: number;
  isTechnicalError?: string;
  errorCode?: string;
}): Promise<number> {
  const params: Record<string, any> = {
    searched,
  };

  if (clientId) params.clientId = clientId;
  if (isTechnicalError) params.isTechnicalError = isTechnicalError;
  if (errorCode) params.errorCode = errorCode;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.ERROR_PATTERN.COUNT);
  const response = await api.get<any>(endpoint, params);
  
  // Handle different response formats
  if (typeof response === 'number') {
    return response;
  } else if (response && typeof response === 'object') {
    // Try different possible property names
    return response.count || response.errorPatternCount || response.total || 0;
  }
  
  return 0;
}

// GET error pattern by ID
export async function fetchErrorPatternById({
  clientCode,
  patternId,
}: {
  clientCode: string;
  patternId: number;
}): Promise<ErrorPatternData> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.ERROR_PATTERN.LIST}${patternId}`);
  const response = await api.get<any>(endpoint);
  // Backend may return { object: {...}, success, messages } - extract object if present
  return (response?.object ?? response) as ErrorPatternData;
}

// CREATE error pattern
export async function createErrorPattern({
  clientCode,
  payload,
}: {
  clientCode: string;
  payload: any;
}): Promise<ErrorPatternData> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.ERROR_PATTERN.CREATE);
  return api.post<ErrorPatternData>(endpoint, payload);
}

// UPDATE error pattern - PUT to base path errorPatternMaster/ (id in body, not URL)
export async function updateErrorPattern({
  clientCode,
  payload,
}: {
  clientCode: string;
  payload: {
    id: number;
    clientId: number;
    errorCode: string;
    pattern: string;
    isTechnicalError: string;
    isRetryRequired?: string | null;
    createdBy?: number;
    creationDate?: number;
    lastUpdateDate?: number;
    lastUpdatedBy?: number;
  };
}): Promise<ErrorPatternData> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.ERROR_PATTERN.UPDATE);
  return api.put<ErrorPatternData>(endpoint, payload);
}

// DELETE error pattern
export async function deleteErrorPattern({
  clientCode,
  patternId,
}: {
  clientCode: string;
  patternId: number;
}): Promise<void> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.ERROR_PATTERN.DELETE}${patternId}`);
  return api.delete(endpoint);
}

// GET error pattern enums
export async function fetchErrorPatternEnums({
  clientCode,
}: {
  clientCode: string;
}): Promise<any> {
  const params = {
    firstResult: -1,
    maxResult: 0,
  };
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.ERROR_PATTERN.ENUMS);
  return api.get<any>(endpoint, params);
}

// GET error patterns by clientId - returns { id, errorCode } for Failure Mail Config
// Handles: array of arrays [id, clientId, pattern, errorCode, ...], array of objects { id, errorCode }, or wrapped { object: [...] }
export async function fetchErrorPatternsByClientId({
  clientCode,
  clientId,
}: {
  clientCode: string;
  clientId: number;
}): Promise<{ id: number; errorCode: string }[]> {
  const params = {
    firstResult: -1,
    maxResult: 0,
    clientId,
  };
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.ERROR_PATTERN.LIST);
  const raw: any = await api.get<any>(endpoint, params);
  const response = Array.isArray(raw) ? raw : raw?.object ?? raw?.data ?? [];
  if (!Array.isArray(response)) return [];
  return response
    .map((p: any) => {
      if (Array.isArray(p) && p.length >= 4) {
        return { id: p[0], errorCode: p[3] ?? '' };
      }
      if (typeof p === 'object' && p !== null) {
        const id = p.id ?? p.ID ?? 0;
        const errorCode = p.errorCode ?? p.error_code ?? '';
        return { id, errorCode };
      }
      return null;
    })
    .filter((p): p is { id: number; errorCode: string } => p !== null && !!p.errorCode);
}
